import { useTranslation } from '@/i18n';
import type { ButtonV2Props } from '@moneyforward/ap-frontend-components';
import {
  IconHelp,
  IconTrailing,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import { GetImportHistoryErrorsCsvDownloadsCsvEncoding } from 'ap-openapi';
import classnames from 'classnames/bind';
import React, { useMemo } from 'react';
import styles from '../ErrorDetailDrawer.module.scss';

const cx = classnames.bind(styles);

export type UseErrorDetailDownloadMenuParams = {
  onDownload?: (
    encoding: GetImportHistoryErrorsCsvDownloadsCsvEncoding
  ) => void;
};

type DownloadMenuItem = {
  key: 'utf8_bom' | 'shift_jis';
  label: React.ReactNode;
  onClick: () => void;
};

export type UseErrorDetailDownloadMenuResult = {
  downloadItems: DownloadMenuItem[];
  downloadButtonProps: ButtonV2Props;
};

export function useErrorDetailDownloadMenu({
  onDownload,
}: UseErrorDetailDownloadMenuParams): UseErrorDetailDownloadMenuResult {
  const { t } = useTranslation();

  const downloadItems = useMemo(() => {
    if (!onDownload) return [];
    return [
      {
        key: 'utf8_bom' as const,
        label: (
          <span className={cx('download-option')}>
            {t('import_export_history_error_detail_download_utf8_bom')}
            <Tooltip
              title={t(
                'import_export_history_error_detail_download_utf8_bom_tooltip'
              )}
              placement='top'
            >
              <span className={cx('download-option-icon')}>
                <IconHelp size={14} />
              </span>
            </Tooltip>
          </span>
        ),
        onClick: () => onDownload('utf-8'),
      },
      {
        key: 'shift_jis' as const,
        label: (
          <span className={cx('download-option')}>
            {t('import_export_history_error_detail_download_shift_jis')}
            <Tooltip
              title={t(
                'import_export_history_error_detail_download_shift_jis_tooltip'
              )}
              placement='top'
            >
              <span className={cx('download-option-icon')}>
                <IconHelp size={14} />
              </span>
            </Tooltip>
          </span>
        ),
        onClick: () => onDownload('shift_jis'),
      },
    ];
  }, [onDownload, t]);

  const downloadButtonProps =
    useMemo((): UseErrorDetailDownloadMenuResult['downloadButtonProps'] => {
      return {
        name: t('import_export_history_error_detail_download'),
        children: t('import_export_history_error_detail_download'),
        color: 'primary',
        isSecondary: true,
        isTertiary: false,
        rightIcon: <IconTrailing height={16} width={16} />,
      };
    }, [t]);

  return { downloadItems, downloadButtonProps };
}
